import { useEffect, useMemo, useState } from "react";
import withRouter from "hoc/withRouter";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { snack } from "components/Snack/Snack";
import trainRunsService from "services/admin/trainRunsService";
import wagonsService from "services/admin/wagonsService";

const RunComposition = ({ router }: { router: any }) => {
  const {
    params: { id },
    navigate,
  } = router;
  const orgID = localStorage.getItem("orgID") || "";
  const [selected, setSelected] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);

  const { data: run, isLoading: runLoading } = useQuery({
    queryKey: ["train-run", id],
    queryFn: async () => (await trainRunsService.get(id)).data,
  });

  const { data: wagonsResp, isLoading: wagonsLoading } = useQuery({
    queryKey: ["wagons", { orgID, active: 1 }],
    queryFn: async () => (await wagonsService.getAll({ active: "1" })).data,
  });

  useEffect(() => {
    if ((run as any)?.assignments) {
      setSelected((run as any).assignments.map((a: any) => a.wagonID));
    }
  }, [run]);

  const wagons: any[] = (wagonsResp as any)?.data || [];
  const num = (n: any) => Number(n || 0).toLocaleString("ru-RU");

  const toggle = (wid: number) =>
    setSelected((s) => (s.includes(wid) ? s.filter((x) => x !== wid) : [...s, wid]));

  const capacityTotal = useMemo(
    () =>
      wagons
        .filter((w) => selected.includes(w.id))
        .reduce((s, w) => s + Number(w.capacityKg || 0), 0),
    [wagons, selected],
  );

  const save = async () => {
    setSaving(true);
    try {
      await trainRunsService.setComposition(
        id,
        selected.map((wagonID, i) => ({ wagonID, position: i + 1 })),
      );
      snack("Состав рейса сохранён");
      setTimeout(() => navigate(-1), 500);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  if (runLoading || wagonsLoading) {
    return (
      <div className="text-center m-t-32">
        <CircularProgress />
      </div>
    );
  }

  const r: any = run;
  const title = r
    ? `Состав рейса · поезд ${r.train?.number || `#${r.trainID}`} · ${r.departureDate || "—"}`
    : "Состав рейса";

  return (
    <div>
      <h2 className="text-2xl m-b-16">{title}</h2>
      {r && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {(r.originStation?.name || "—") + " → " + (r.destinationStation?.name || "—")}
        </Typography>
      )}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          {wagons.length === 0 ? (
            <Typography color="text.secondary">
              Нет активных вагонов. Сначала добавьте вагоны в разделе «Вагоны».
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox"></TableCell>
                  <TableCell>Номер</TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell align="right">Грузоподъёмность, кг</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {wagons.map((w) => (
                  <TableRow key={w.id} hover onClick={() => toggle(w.id)} sx={{ cursor: "pointer" }}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selected.includes(w.id)} />
                    </TableCell>
                    <TableCell>{w.number}</TableCell>
                    <TableCell>{w.type}</TableCell>
                    <TableCell align="right">{num(w.capacityKg)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
        <Typography variant="subtitle1">
          Выбрано вагонов: <strong>{selected.length}</strong>
        </Typography>
        <Typography variant="subtitle1">
          Σ грузоподъёмность: <strong>{num(capacityTotal)} кг</strong>
        </Typography>
        <Button variant="contained" onClick={save} disabled={saving}>
          Сохранить состав
        </Button>
        <Button onClick={() => navigate(-1)}>Отмена</Button>
      </Box>
    </div>
  );
};

export default withRouter(RunComposition);
